import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Modal, DataTable, Toolbar, exportCsv, Plus, toast } from "@/pages/app/kit";

const FLAG_KEYS = ["soil_planning","harvest_guarantee","market_integrity","investor_data_room","proposal_generator","logistics","biological_recovery","public_crop_at_risk","military_buyer","finance_partner","ai_assisted_matching"];

export function AdminPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [settings, setSettings] = useState(null);

  const load = () => api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const put = async (key, value) => { try { await api.put("/admin/settings", { key, value }); toast.success("OK"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const toggleFlag = (k) => { const flags = { ...settings.feature_flags, [k]: !settings.feature_flags[k] }; put("feature_flags", flags); };

  if (!settings) return <div className="text-mutedtext">…</div>;

  return (
    <div>
      <PageTitle title={t("dashboard.settings")} subtitle={L("Konfiguracja platformy","Platform configuration")} />
      <div className="space-y-6">
        <Panel title={t("dashboard.flags")}>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {FLAG_KEYS.map((k) => (
              <label key={k} className="flex items-center justify-between rounded-md border border-subtle px-3 py-2 text-sm">
                <span className="text-charcoal">{k}</span>
                <input type="checkbox" checked={!!settings.feature_flags?.[k]} onChange={() => toggleFlag(k)} data-testid={`flag-${k}`} />
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-mutedtext">{L("AI-assisted matching domyślnie wyłączone.","AI-assisted matching is off by default.")}</p>
        </Panel>

        <Panel title={L("Kontakt","Contact")}>
          <ContactEditor value={settings.contact} onSave={(v) => put("contact", v)} />
        </Panel>

        <Panel title={L("Progi ryzyka","Risk thresholds")}>
          <ThresholdEditor value={settings.risk_thresholds} onSave={(v) => put("risk_thresholds", v)} />
        </Panel>

        <Panel title={L("Bezpieczeństwo","Security")}>
          <p className="text-sm text-mutedtext">{L("Po rotacji haseł unieważnij wszystkie aktywne sesje. Użytkownicy będą musieli zalogować się ponownie.","After a credential rotation, invalidate all active sessions. Users must sign in again.")}</p>
          <Btn variant="danger" className="mt-3" data-testid="invalidate-sessions-btn"
            onClick={async () => { try { const r = await api.post("/admin/invalidate-sessions"); toast.success(r.data.message); } catch (e) { toast.error("Failed"); } }}>
            {L("Unieważnij wszystkie sesje","Invalidate all sessions")}
          </Btn>
        </Panel>
      </div>
    </div>
  );
}

function ContactEditor({ value, onSave }) {
  const [v, setV] = useState(value || {});
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Email"><Input value={v.email || ""} onChange={(e) => setV({ ...v, email: e.target.value })} data-testid="contact-email-setting" /></Field>
      <Field label="Pilot region"><Input value={v.pilot_region || ""} onChange={(e) => setV({ ...v, pilot_region: e.target.value })} /></Field>
      <div className="sm:col-span-2"><Btn onClick={() => onSave(v)} data-testid="save-contact">Save</Btn></div>
    </div>
  );
}
function ThresholdEditor({ value, onSave }) {
  const [v, setV] = useState(value || {});
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Field label="Harvest days window"><Input type="number" value={v.days_window ?? 10} onChange={(e) => setV({ ...v, days_window: parseInt(e.target.value) || 0 })} /></Field>
      <Field label="Min coverage %"><Input type="number" value={v.min_coverage_pct ?? 60} onChange={(e) => setV({ ...v, min_coverage_pct: parseInt(e.target.value) || 0 })} /></Field>
      <Field label="Planning window"><Input type="number" value={v.planning_window ?? 30} onChange={(e) => setV({ ...v, planning_window: parseInt(e.target.value) || 0 })} /></Field>
      <div className="sm:col-span-3"><Btn onClick={() => onSave(v)} data-testid="save-thresholds">Save</Btn></div>
    </div>
  );
}

export function UsersPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "", role: "farmer", organization_name: "", region: "lubelskie", verification_status: "verified" });
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async (e) => { e.preventDefault(); try { await api.post("/admin/users", f); toast.success("OK"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const updateRole = async (id, role) => { await api.patch(`/admin/users/${id}`, { role }); load(); };

  const ROLES = ["farmer","buyer","processor","logistics","coordinator","quality","agronomist","finance","investor","admin","super_admin"];
  const filtered = rows.filter((r) => (r.email + r.name).toLowerCase().includes(search.toLowerCase()));
  const cols = [
    { key: "name", label: "Name", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-mutedtext">{r.email}</div></div> },
    { key: "organization_name", label: "Organization" },
    { key: "role", label: "Role", render: (r) => <Select value={r.role} onChange={(e) => updateRole(r.id, e.target.value)} className="text-xs">{ROLES.map((x)=><option key={x}>{x}</option>)}</Select> },
    { key: "verification_status", label: "Status" },
    { key: "is_demo", label: "Demo", render: (r) => r.is_demo ? "yes" : "" },
  ];
  return (
    <div>
      <PageTitle title={t("dashboard.users")} action={<Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-user-btn">{t("cta.create")}</Btn>} />
      <Toolbar search={search} setSearch={setSearch} onExport={() => exportCsv("users.csv", filtered)} />
      <DataTable columns={cols} rows={filtered} empty={t("empty")} testid="users-table" />
      <Modal open={open} onClose={() => setOpen(false)} title={t("dashboard.users")}>
        <form onSubmit={create} className="space-y-4">
          <Field label="Name" required><Input value={f.name} onChange={set("name")} required /></Field>
          <Field label="Email" required><Input type="email" value={f.email} onChange={set("email")} required /></Field>
          <Field label="Password" required><Input type="password" value={f.password} onChange={set("password")} required /></Field>
          <Field label="Role"><Select value={f.role} onChange={set("role")}>{ROLES.map((x)=><option key={x}>{x}</option>)}</Select></Field>
          <Field label="Organization"><Input value={f.organization_name} onChange={set("organization_name")} /></Field>
          <Btn type="submit" data-testid="user-submit">{t("cta.create")}</Btn>
        </form>
      </Modal>
    </div>
  );
}

export function LeadsPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const load = () => api.get("/admin/leads").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/leads/${id}`, { status }); load(); };
  const filtered = rows.filter((r) => (r.name + r.organization + r.role).toLowerCase().includes(search.toLowerCase()));
  const cols = [
    { key: "role", label: "Role" },
    { key: "name", label: "Name", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-mutedtext">{r.organization}</div></div> },
    { key: "email", label: "Contact", render: (r) => <div className="text-xs">{r.email}<br/>{r.phone}</div> },
    { key: "message", label: "Message", render: (r) => <span className="text-mutedtext">{r.message}</span> },
    { key: "status", label: "Status", render: (r) => <Select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="text-xs">{["New","Contacted","Qualified","Closed"].map((x)=><option key={x}>{x}</option>)}</Select> },
  ];
  return (
    <div>
      <PageTitle title={t("dashboard.leads")} subtitle={L("Zgłoszenia z formularzy publicznych","Public interest submissions")} />
      <Toolbar search={search} setSearch={setSearch} onExport={() => exportCsv("leads.csv", filtered)} />
      <DataTable columns={cols} rows={filtered} empty={t("empty")} testid="leads-table" />
    </div>
  );
}

export function AuditPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const filtered = rows.filter((r) => (r.action + r.entity + (r.user_email||"")).toLowerCase().includes(search.toLowerCase()));
  const cols = [
    { key: "created_at", label: "Time", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "user_email", label: "User", render: (r) => <div>{r.user_email}<div className="text-xs text-mutedtext">{r.user_role}</div></div> },
    { key: "action", label: "Action", render: (r) => <span className="rounded bg-secondary px-2 py-0.5 text-xs font-medium">{r.action}</span> },
    { key: "entity", label: "Entity" },
    { key: "entity_id", label: "ID", render: (r) => <span className="font-mono text-xs">{String(r.entity_id).slice(0,10)}</span> },
  ];
  return (
    <div>
      <PageTitle title={t("dashboard.audit")} subtitle={L("Dziennik tylko do dopisywania","Append-only audit log")} />
      <Toolbar search={search} setSearch={setSearch} />
      <DataTable columns={cols} rows={filtered} empty={t("empty")} testid="audit-table" />
    </div>
  );
}
